/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig } from '../lib/config.js';

export function init() {
const mapEl = document.getElementById('map');
if (!mapEl) return;

const mapMetadataURL = getConfig()?.endpoints?.mapMetadata;
if (!mapMetadataURL) return;

const baseDelay = 1500; // start fast while editing
const maxDelay = 30000; // cap backoff at 30s
const minDelay = 1500; // floor
let lastUpdatedAt; // number or undefined on first run // ES6: let
let inFlight = false; // ES6: let
let delay = baseDelay; // ES6: let
const stopped = false; // ES6: let

function visible() {
    // IE11 uses msHidden; modern browsers use hidden
    return !(document.hidden || document.msHidden);
}

function jitter(ms) {
    // Small random spread to avoid herding; ~0–250 ms
    return ms + Math.floor(Math.random() * 250);
}

function schedule(nextMs) {
    if (stopped) return;
    setTimeout(
        tick,
        jitter(Math.max(minDelay, Math.min(nextMs, maxDelay)))
    );
}

function setDelayOnSuccessNoChange() {
    // Gradually ease toward a slower cadence (but never exceed maxDelay)
    delay = Math.min(maxDelay, Math.floor(delay * 1.25));
}

function setDelayOnSuccessChangeSeen() {
    // Be responsive again after confirming freshness
    delay = baseDelay;
}

function setDelayOnHidden() {
    // Slow down when tab is hidden
    delay = Math.min(maxDelay, Math.max(delay, 5000));
}

function setDelayOnError() {
    // Backoff aggressively on network errors
    delay = Math.min(maxDelay, Math.max(minDelay, Math.floor(delay * 1.7)));
}

function withCacheBuster(url) {
    try {
        const u = new URL(url, window.location.href);
        // overwrite cb each time to defeat intermediary caches
        u.searchParams.set('cb', String(Date.now()));
        return u.toString();
    } catch (e) {
        const sep = url.indexOf('?') === -1 ? '?' : '&';
        return `${url + sep}cb=${Date.now()}`;
    }
}

function fetchMetadata() {
    return fetch(withCacheBuster(mapMetadataURL), {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin'
    })
        .then((r) => {
            if (!r.ok) throw r;
            return r.json();
        })
        .then((json) =>
            json && json.updatedAt != null ? Number(json.updatedAt) : null
        );
}

function tick() {
    if (stopped || inFlight) {
        schedule(delay);
        return;
    }
    if (!visible()) {
        setDelayOnHidden();
        schedule(delay);
        return;
    }

    inFlight = true;

    fetchMetadata()
        .then((updatedAt) => {
            if (updatedAt == null || Number.isNaN(updatedAt)) {
                // Unexpected payload → treat as error to back off a bit
                setDelayOnError();
            } else if (
                lastUpdatedAt !== undefined &&
                updatedAt !== lastUpdatedAt
            ) {
                // Change detected → hard reload
                window.location.reload();
                // Avoid getting stuck if reload is blocked
                lastUpdatedAt = updatedAt;
                inFlight = false;
                schedule(delay);
                return;
            } else {
                // No change
                setDelayOnSuccessNoChange();
                if (lastUpdatedAt === undefined) {
                    // First successful read: remember baseline
                    lastUpdatedAt = updatedAt;
                }
            }
            inFlight = false;
            schedule(delay);
        })
        .catch(() => {
            setDelayOnError();
            inFlight = false;
            schedule(delay);
        });
}

function onVisibilityChange() {
    if (visible()) {
        // Speed back up when user returns
        delay = baseDelay;
        if (!inFlight) schedule(0);
    } else {
        setDelayOnHidden();
    }
}

// Network awareness
window.addEventListener('online', () => {
    delay = baseDelay;
    if (!inFlight) schedule(0);
});
window.addEventListener('offline', () => {
    setDelayOnError();
});

if (typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('msvisibilitychange', onVisibilityChange); // IE11
}

// Kick off
schedule(0);
}
